import React, { useEffect, useState } from "react";
import { LanguageCode } from "../../constants/LanguageCode";
import { LanguageJsonModel, CategoryPackageJsonModel } from "../../models/BlockTransportSystemJsonModel";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { inject, observer } from "mobx-react";
import BlockKitStore from "../../stores/BlockKitStore";
import StudioUserStore from "../../stores/StudioUserStore";
import { UserPermissionModel } from "../../controllers/StudioAdminController";
import { useTranslation } from "react-i18next";

interface IProps {
  blockKitStore?: BlockKitStore;
  studioUserStore?: StudioUserStore;
  categoryPackage?: CategoryPackageJsonModel;
  isShow: boolean;
  isCreateMode: boolean;
  onClickApply: (categoryPackage: CategoryPackageJsonModel) => Promise<void>;
  onClickClose: () => void;
}

const CategoryPackageInfoCard: React.FC<IProps> = inject("blockKitStore", "studioUserStore")(observer(({
  blockKitStore,
  studioUserStore,
  categoryPackage,
  isShow,
  isCreateMode,
  onClickApply,
  onClickClose
}: IProps) => {
  const [inputs, setInputs] = useState({
    [LanguageCode.koKR]: categoryPackage?.lang?.find(language => language.key === LanguageCode.koKR)?.name || "",
    [LanguageCode.enUS]: categoryPackage?.lang?.find(language => language.key === LanguageCode.enUS)?.name || "",
    [LanguageCode.zhCN]: categoryPackage?.lang?.find(language => language.key === LanguageCode.zhCN)?.name || "",
    [LanguageCode.zhHK]: categoryPackage?.lang?.find(language => language.key === LanguageCode.zhHK)?.name || "",
    [LanguageCode.viVN]: categoryPackage?.lang?.find(language => language.key === LanguageCode.viVN)?.name || "",
    [LanguageCode.jaJP]: categoryPackage?.lang?.find(language => language.key === LanguageCode.jaJP)?.name || "",
    [LanguageCode.mnMN]: categoryPackage?.lang?.find(language => language.key === LanguageCode.mnMN)?.name || "",
    [LanguageCode.thTH]: categoryPackage?.lang?.find(language => language.key === LanguageCode.thTH)?.name || "",
  });
  const [viewType, setViewType] = useState<string>(categoryPackage?.viewType ?? "");
  const [priority, setPriority] = useState<number>(categoryPackage?.priority ?? 3);
  const [permissions, setPermissions] = useState<UserPermissionModel[]>([]);
  const [isWorking, setIsWorking] = useState<boolean>(false);
  const { t } = useTranslation();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setInputs({
      ...inputs,
      [name]: value
    });
  };

  useEffect(() => {
    if (categoryPackage?.id) {
      blockKitStore?.studioController.getEntityPermissionsAsync("category-packages", categoryPackage.id).then(setPermissions);
    }
  }, []);

  useEffect(() => {
    setInputs({
      [LanguageCode.koKR]: categoryPackage?.lang?.find(language => language.key === LanguageCode.koKR)?.name || "",
      [LanguageCode.enUS]: categoryPackage?.lang?.find(language => language.key === LanguageCode.enUS)?.name || "",
      [LanguageCode.zhCN]: categoryPackage?.lang?.find(language => language.key === LanguageCode.zhCN)?.name || "",
      [LanguageCode.zhHK]: categoryPackage?.lang?.find(language => language.key === LanguageCode.zhHK)?.name || "",
      [LanguageCode.viVN]: categoryPackage?.lang?.find(language => language.key === LanguageCode.viVN)?.name || "",
      [LanguageCode.jaJP]: categoryPackage?.lang?.find(language => language.key === LanguageCode.jaJP)?.name || "",
      [LanguageCode.mnMN]: categoryPackage?.lang?.find(language => language.key === LanguageCode.mnMN)?.name || "",
      [LanguageCode.thTH]: categoryPackage?.lang?.find(language => language.key === LanguageCode.thTH)?.name || "",
    });
    setViewType(categoryPackage?.viewType ?? "");
    setPriority(categoryPackage?.priority ?? 3);
  }, [categoryPackage]);

  const getLanguages = () => {
    const languages = Object.entries(inputs).map((value) => {
      return {
        key: value[0],
        name: value[1],
      } as LanguageJsonModel;
    });
    return languages;
  }

  const getDefaultName = () => {
    return inputs[LanguageCode.koKR];
  }

  return (
    <Modal show={isShow} onHide={() => onClickClose()}>
      <Modal.Header closeButton>{t("category-package")}</Modal.Header>
      <Modal.Body>
        <Form.Group as={Row} className="mb-1">
          <Form.Label column sm={3}>{t("id")}</Form.Label>
          <Col>
            <Form.Control type="text" readOnly value={categoryPackage?.id} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-1">
          <Form.Label column sm={3}>{t("name")}</Form.Label>
          <Col>
            <Form.Control type="text" readOnly value={categoryPackage?.name} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-1">
          <Form.Label column sm={3}>{t("view-type")}</Form.Label>
          <Col>
            <Form.Select
              value={viewType}
              onChange={(e) => {
                setViewType(e.target.value);
              }}
            >
              <option value="">{t("none")}</option>
              <option value="spreadView">{t("view-type-spread")}</option>
              <option value="thumbnailView">{t("view-type-thumbnail")}</option>
              <option value="categoryThumbnailView">{t("view-type-category-thumbnail")}</option>
            </Form.Select>
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-1">
          <Form.Label column sm={3}>{t("priority")}</Form.Label>
          <Col>
            <Form.Control
              type="number"
              min={1}
              value={priority ?? 3}
              onChange={(e) => {
                setPriority(e.target.value === "" ? 3 : +e.target.value);
              }} />
          </Col>
        </Form.Group>
        <Form.Label>{t("name-by-country")} ({t("saved-default-name-message")})</Form.Label>
        {Object.values(LanguageCode).map(lang => {
          return (
            <Form.Group key={lang} as={Row} className="mb-1">
              <Form.Label column sm={3}>{`${lang}`}</Form.Label>
              <Col>
                <Form.Control type={"text"} name={lang} value={inputs[lang]} onChange={onChange} />
              </Col>
            </Form.Group>
          );
        })}
      </Modal.Body>
      <Modal.Footer>
        <Button
          disabled={(!isCreateMode && !studioUserStore?.hasWritePermission(permissions)) || isWorking}
          onClick={async () => {
            setIsWorking(true);
            try {
              if (!confirm(t("save-confirmation-message"))) {
                return;
              }

              const lang = getLanguages();
              const name = getDefaultName();
              await onClickApply({
                ...categoryPackage,
                name,
                lang,
                viewType,
                priority
              });

            } finally {
              setIsWorking(false);
            }

          }}>
          {isWorking &&
            <Spinner
              className="me-1"
              animation="border"
              size="sm"
              role="status"
            />}
          {t("button-save")}
        </Button>
        <Button
          disabled={isWorking}
          variant="secondary"
          onClick={() => {
            onClickClose();
          }}>
          {t("button-close")}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}));

export default CategoryPackageInfoCard;